import React, { useMemo, useState } from "react";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";

function categoriseInternational(bmi, t) {
  if (bmi === null) return null;
  if (bmi < 18.5) return { key: "underweight", label: t("bmi.cat.underweight"), tone: "warning" };
  if (bmi < 25) return { key: "normal", label: t("bmi.cat.normal"), tone: "success" };
  if (bmi < 30) return { key: "overweight", label: t("bmi.cat.overweight"), tone: "warning" };
  if (bmi < 35) return { key: "obese1", label: t("bmi.cat.obese1"), tone: "danger" };
  if (bmi < 40) return { key: "obese2", label: t("bmi.cat.obese2"), tone: "danger" };
  return { key: "obese3", label: t("bmi.cat.obese3"), tone: "danger" };
}

function categoriseAsiaPacific(bmi, t) {
  if (bmi === null) return null;
  if (bmi < 18.5) return { key: "underweight", label: t("bmi.cat.underweight"), tone: "warning" };
  if (bmi < 23) return { key: "normal", label: t("bmi.cat.normal"), tone: "success" };
  if (bmi < 25) return { key: "at_risk", label: t("bmi.cat.at_risk"), tone: "warning" };
  if (bmi < 30) return { key: "obese1", label: t("bmi.cat.obese1"), tone: "danger" };
  return { key: "obese2", label: t("bmi.cat.obese2"), tone: "danger" };
}

export default function BMI() {
  const t = useT();
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [criteria, setCriteria] = useState("international");

  const w = parseFloat(weight);
  const h = parseFloat(height);

  const bmi = useMemo(() => {
    if (!w || !h || w <= 0 || h <= 0) return null;
    const m = h / 100;
    return w / (m * m);
  }, [w, h]);

  const cat = useMemo(
    () =>
      bmi === null
        ? null
        : criteria === "international"
        ? categoriseInternational(bmi, t)
        : categoriseAsiaPacific(bmi, t),
    [bmi, criteria, t]
  );

  return (
    <div data-testid="page-bmi">
      <PageHeader title={t("bmi.title")} subtitle={t("bmi.subtitle")} formula={t("bmi.formula")} />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("bmi.weight")} unit="kg" testid="weight">
              <NumberInput
                value={weight}
                onChange={setWeight}
                placeholder={t("bmi.weight.placeholder")}
                min={0}
                testid="weight-input"
              />
            </Field>
            <Field label={t("bmi.height")} unit="cm" testid="height">
              <NumberInput
                value={height}
                onChange={setHeight}
                placeholder={t("bmi.height.placeholder")}
                min={0}
                testid="height-input"
              />
            </Field>
          </div>

          <Field label={t("bmi.criteria")} testid="criteria">
            <div
              className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full"
              role="group"
              data-testid="criteria-group"
            >
              {[
                { v: "international", label: t("bmi.criteria.international") },
                { v: "asia_pacific", label: t("bmi.criteria.asia_pacific") },
              ].map((opt) => {
                const active = criteria === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`criteria-${opt.v}`}
                    onClick={() => setCriteria(opt.v)}
                    className={
                      "flex-1 px-3 py-2.5 text-sm transition-colors duration-100 " +
                      (active
                        ? "bg-sky-700 text-white"
                        : "bg-white text-stone-700 hover:bg-stone-50")
                    }
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Note>{t("bmi.note")}</Note>
        </div>

        <div className="space-y-4">
          <Result
            label={t("bmi.result.bmi")}
            value={bmi !== null ? fmt(bmi, 1) : "—"}
            unit="kg / m²"
            big
            testid="result-bmi"
            accent={cat?.tone === "danger" ? "danger" : cat?.tone === "success" ? "success" : "default"}
          />
          <Result
            label={t("bmi.result.category")}
            value={cat ? cat.label : "—"}
            testid="result-category"
            accent={cat?.tone === "danger" ? "danger" : cat?.tone === "success" ? "success" : "default"}
          />
          <FormulaBox testid="bmi-legend">
            {criteria === "international"
              ? t("bmi.legend.international")
              : t("bmi.legend.asia_pacific")}
          </FormulaBox>
          {!bmi && (
            <div className="text-xs text-stone-500" data-testid="bmi-empty">
              {t("bmi.empty")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
